export type ForcedProvider = 'microsoft' | 'google';

const FORCED_SSO_ATTEMPT_KEY = 'forced_sso_attempt';
const DUPLICATE_GUARD_MS = 5_000;

/**
 * `?provider=` value that asks for the email/password form.
 *
 * It is not a `ForcedProvider`: there is no redirect to start, the value
 * only reveals a form that is otherwise not rendered.
 */
const CREDENTIALS_PROVIDER = 'email';

/**
 * True when the URL explicitly asks for the email/password form.
 *
 * Credentials are opt-in per visit: the login screen offers SSO only, and
 * the form appears for `?provider=email` alone. Nothing is persisted, so
 * the next visit is back to SSO-only.
 */
export const offersCredentials = (search: string): boolean =>
  new URLSearchParams(search).get('provider')?.toLowerCase() === CREDENTIALS_PROVIDER;

export const getForcedProvider = (search: string): ForcedProvider | null => {
  const provider = new URLSearchParams(search).get('provider')?.toLowerCase();
  return provider === 'microsoft' || provider === 'google' ? provider : null;
};

/**
 * Which provider the login screen must start on its own.
 *
 * Only `?provider=` starts a redirect, so a login screen reached directly
 * stays a manual stop. When the host application restricts itself to a
 * single provider, a query parameter naming another one is ignored rather
 * than honoured: the URL must not widen the ways into the application.
 */
export const resolveLoginProvider = (
  search: string,
  enforced?: ForcedProvider | null,
): ForcedProvider | null => {
  const requested = getForcedProvider(search);
  if (!requested || (enforced && requested !== enforced)) {
    return null;
  }
  return requested;
};

export const shouldStartForcedSso = (provider: ForcedProvider): boolean => {
  const current = sessionStorage.getItem(FORCED_SSO_ATTEMPT_KEY);
  if (current) {
    const [currentProvider, timestamp] = current.split(':');
    if (currentProvider === provider && Date.now() - Number(timestamp) < DUPLICATE_GUARD_MS) {
      return false;
    }
  }
  sessionStorage.setItem(FORCED_SSO_ATTEMPT_KEY, `${provider}:${Date.now()}`);
  return true;
};

export const clearForcedSsoAttempt = () => sessionStorage.removeItem(FORCED_SSO_ATTEMPT_KEY);
